import { subject } from "@casl/ability";

import type { AppAbility } from "@recobra/core";
import { OPEN_TASK_STATUSES } from "@recobra/schemas/task";
import type { Task } from "@recobra/schemas/task";

/**
 * `needs_assignee` is a caller who would be allowed once someone holds the
 * task — the server rejects completing an unassigned task with
 * `task_unassigned`. `denied` surfaces no execution affordance at all.
 * Mirrors the server's execution gate.
 */
export type TaskExecutionState = "allowed" | "needs_assignee" | "denied";
export type TaskCompletionBlocker = "assignee" | "permission";

export const resolveTaskExecutionState = (
  task: Task | null | undefined,
  ability: AppAbility,
): TaskExecutionState => {
  if (!task || !OPEN_TASK_STATUSES.includes(task.status)) return "denied";
  if (!task.assignedTo) {
    return ability.can("executeUnowned", "Task") ? "needs_assignee" : "denied";
  }

  return ability.can("execute", subject("Task", task)) ? "allowed" : "denied";
};

export const resolveTaskCompletionBlocker = (
  task: Pick<Task, "status"> | null | undefined,
  executionState: TaskExecutionState,
): TaskCompletionBlocker | null => {
  if (!task || !OPEN_TASK_STATUSES.includes(task.status)) return null;
  if (executionState === "needs_assignee") return "assignee";
  if (executionState === "denied") return "permission";

  return null;
};

export const taskContextRoute = (task: Task | null | undefined) => {
  if (!task) return null;
  if (task.contextType === "client") {
    return { to: "/clients/$id" as const, params: { id: task.contextId } };
  }
  if (task.contextType !== "case" || !task.contextCaseNumber) return null;
  if (task.system === "review_case_creation") {
    return {
      to: "/cases/$caseNumber/finish-creation" as const,
      params: { caseNumber: String(task.contextCaseNumber) },
    };
  }

  return {
    to: "/cases/$caseNumber" as const,
    params: { caseNumber: String(task.contextCaseNumber) },
  };
};
